<script setup lang="ts">
import {AutoComplete} from "@progress/kendo-vue-dropdowns";
import {useCharacterDataStore} from "@/stores/character-data";
import {usePredicateStore} from "@/stores/predicate-store";
import {onBeforeMount} from "vue";
import {storeToRefs} from "pinia";
import {useAppDataStore} from "@/stores/app-data";
import {useAuthDataStore} from "@/stores/auth-data";
import {useTypeAheadLookup} from "@/composables/useTypeAheadLookup";
import {buildWorldPredicates} from "@/composables/buildPredicates";
import type {WorldInterface} from "@/types/worlds";

const emit = defineEmits(['worldSelected', 'worldCleared']);
const props = defineProps(['placeholder','includeAll','libraryOnly']);
const store = useAppDataStore();
const auth = useAuthDataStore();
const characterData = useCharacterDataStore();
const predicateStore = usePredicateStore();
storeToRefs(characterData)
storeToRefs(predicateStore)

onBeforeMount(() => {
  if (store.system && (store.system?.uuid !== predicateStore.system?.uuid)) {
    predicateStore.system = store.system;
  }
})

// The type-ahead keeps to itself. It is a lookup, not a filter: the text typed into it goes to
// the API, and the answer is this finder's own (useTypeAheadLookup) — only the latest lookup's.
// Neither the shared world keyword (worldFilters.stringPartial) nor the shared world list
// (store.worlds) is written from here. Both belong to whichever world surface is on screen — the
// world list, the guided sources step, a world browser — and typing here used to re-filter that
// surface, and picking a world used to clear its search behind the text still in its search box.
//
// Its scope is its own too. Library-only offers unowned worlds alone (the Source chip); otherwise
// whether it offers only the user's own worlds is its include-all and nothing else: it used to write that into the shared owned-only flag as it mounted, and read the
// flag back for each lookup — so with several finders on screen the one that mounted last set
// the scope of them all, and the flag was changed under the world list and a world's element
// tab, whose flag it is. And it looks up live worlds whatever the Trash view says: a world is
// picked here to filter by or to link to, and the trashed things on screen belong to worlds that
// are not trashed.
const { choices, onKeyUp } = useTypeAheadLookup<WorldInterface>((text) =>
    store.searchWorlds(store.system, buildWorldPredicates(
        {
          system: store.system, trashed: false,
          ownedOnly: !props.includeAll && !props.libraryOnly, unownedOnly: !!props.libraryOnly,
          excludePremium: !auth.hasPremiumAccess,
        }, text,
    )))

const onWorldSelected = (event: any) => {
  console.debug("WorldFinder | onWorldSelected | ", event)

  if (event.item) {
    emit('worldSelected', event);
  }
  else if (event.target?.value === '') {
    emit('worldCleared', event);
  }
}
</script>

<template>
  <AutoComplete
      class="world-finder"
      text-field="name"
      :data-items="choices"
      :placeholder="props.placeholder"
      @keyup="onKeyUp"
      @change="onWorldSelected"
  ></AutoComplete>
</template>
